import React, { useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import dayjs from "dayjs";

import Input from "components/bannerInput";
import Button from "components/button";
import PhoneNumber from "components/phoneInput";
import SelectOptions from "components/selectOptions";
import PassportField from "components/upload-passport/input-field";
import { useTranslation } from "i18n";

import EntryFeeDateField from "../components/date-field";

const mapScannedGender = (sex) => {
  const value = String(sex || "").toUpperCase();
  if (value === "M" || value === "MALE") return "Male";
  if (value === "F" || value === "FEMALE") return "Female";
  return "";
};

const Apply = ({ setBody, handleStep, setPassportFile }) => {
  const { t } = useTranslation();
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const gender = [
    { value: "Male", label: t("entryFee.form.male") },
    { value: "Female", label: t("entryFee.form.female") },
  ];
  const yerOrNo = [
    { value: "Yes", label: t("common.yes") },
    { value: "No", label: t("common.no") },
  ];
  const [code, setCode] = useState();
  const [showTravelerFields, setShowTravelerFields] = useState(false);
  const [scannedPassport, setScannedPassport] = useState(false);
  const [scanError, setScanError] = useState("");
  const [travelerError, setTravelerError] = useState("");

  const validationSchema = Yup.object().shape({
    dayOfArrival: Yup.mixed()
      .required()
      .label(t("entryFee.form.dayOfArrival")),
    ticketPurchasedFromUs: Yup.string()
      .required()
      .label(t("entryFee.form.thisField")),
    isEmergency: Yup.string().required().label(t("entryFee.form.thisField")),
    firstName: Yup.string().required().label(t("account.firstName")),
    lastName: Yup.string().required().label(t("account.lastName")),
    dateOfBirth: Yup.mixed().required().label(t("account.dateOfBirth")),
    phoneNumber: Yup.string().required().label(t("account.phoneNumber")),
    gender: Yup.string().required().label(t("entryFee.form.gender")),
    passportNumber: Yup.string()
      .required()
      .label(t("entryFee.form.passportNumber")),
  });

  const handelSubmit = async (values) => {
    const body = { ...values };
    body.dayOfArrival = dayjs(body.dayOfArrival).format("YYYY-MM-DD");
    body.dateOfBirth = dayjs(body.dateOfBirth).format("YYYY-MM-DD");
    body.ticketPurchasedFromUs =
      body.ticketPurchasedFromUs === "Yes" ? true : false;
    body.isEmergency = body.isEmergency === "Yes" ? true : false;

    setBody(body);
    handleStep();
  };

  return (
    <section className="entry-fee-panel">
      <header className="entry-fee-panel__head">
        <p className="entry-fee-kicker">{t("entryFee.steps.apply")}</p>
        <h2>{t("entryFee.form.title")}</h2>
        <p>{t("entryFee.form.lead")}</p>
      </header>

      <Formik
        initialValues={{
          dayOfArrival: "",
          phoneNumber: "",
          ticketPurchasedFromUs: "",
          isEmergency: "",
          firstName: "",
          lastName: "",
          passportNumber: "",
          dateOfBirth: "",
          gender: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handelSubmit}
        enableReinitialize
        validateOnBlur
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          setFieldTouched,
          touched,
          values,
          setFieldValue,
        }) => (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!showTravelerFields) {
                setTravelerError(t("entryFee.form.chooseTravelerMethod"));
                return;
              }
              handleSubmit(event);
            }}
          >
            <div className="entry-fee-section">
              <h3>{t("entryFee.form.travelSection")}</h3>
              <div className="entry-fee-fields">
                <EntryFeeDateField
                  mandatory
                  label={t("entryFee.form.dayOfArrival")}
                  value={values.dayOfArrival}
                  onChange={(date) => setFieldValue("dayOfArrival", date)}
                  onBlur={() => setFieldTouched("dayOfArrival")}
                  error={touched.dayOfArrival && errors.dayOfArrival}
                  minDate={new Date()}
                />
                <PhoneNumber
                  onChange={handleChange(`phoneNumber`)}
                  error={touched.phoneNumber && errors.phoneNumber}
                  onBlur={() => setFieldTouched("phoneNumber")}
                  name="phoneNumber"
                  label={t("account.phoneNumber")}
                  value={values?.phoneNumber}
                  mandatory
                  setCode={setCode}
                />
                <SelectOptions
                  mandatory
                  label={t("entryFee.form.ticketPurchasedFromUs")}
                  options={yerOrNo}
                  name="ticketPurchasedFromUs"
                  onChange={handleChange("ticketPurchasedFromUs")}
                  error={
                    touched["ticketPurchasedFromUs"] &&
                    errors["ticketPurchasedFromUs"]
                  }
                  value={
                    yerOrNo.find(
                      (item) => item.value === values["ticketPurchasedFromUs"]
                    )?.label
                  }
                  onBlur={() => setFieldTouched("ticketPurchasedFromUs")}
                />
                <SelectOptions
                  mandatory
                  label={t("entryFee.form.isEmergency")}
                  options={yerOrNo}
                  name="isEmergency"
                  onChange={handleChange("isEmergency")}
                  error={touched["isEmergency"] && errors["isEmergency"]}
                  value={
                    yerOrNo.find((item) => item.value === values["isEmergency"])
                      ?.label
                  }
                  onBlur={() => setFieldTouched("isEmergency")}
                />
              </div>
            </div>

            <div className="entry-fee-section">
              <h3>{t("entryFee.form.travelerSection")}</h3>

              <div className="entry-fee-scan">
                <div className="entry-fee-scan__copy">
                  <span className="entry-fee-scan__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 15.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                    </svg>
                  </span>
                  <div>
                    <p>{t("entryFee.form.scanTitle")}</p>
                    <span>
                      {scannedPassport
                        ? t("entryFee.form.scanAgainHint")
                        : t("entryFee.form.scanHint")}
                    </span>
                  </div>
                </div>
                <PassportField
                  setErrImg={setScanError}
                  className="entry-fee-action"
                  label={
                    scannedPassport
                      ? t("entryFee.form.scanAgain")
                      : t("entryFee.form.scanAction")
                  }
                  handlePassportResponse={(travelerData) => {
                    const birthDate = dayjs(travelerData?.birthDate);
                    setFieldValue("firstName", travelerData?.firstName || "");
                    setFieldValue("lastName", travelerData?.lastName || "");
                    setFieldValue("gender", mapScannedGender(travelerData?.sex));
                    setFieldValue(
                      "passportNumber",
                      travelerData?.documentNumber || "",
                    );
                    if (birthDate.isValid()) {
                      setFieldValue("dateOfBirth", birthDate.toDate());
                    }
                    setScannedPassport(true);
                    setShowTravelerFields(true);
                    setScanError("");
                    setTravelerError("");
                  }}
                  onFileSelected={(file) => setPassportFile?.(file)}
                />
              </div>

              {scanError && <p className="entry-fee-alert">{scanError}</p>}

              {!showTravelerFields && (
                <button
                  type="button"
                  className="entry-fee-manual"
                  onClick={() => {
                    setShowTravelerFields(true);
                    setTravelerError("");
                  }}
                >
                  {t("entryFee.form.fillManually")}
                </button>
              )}

              {travelerError && !showTravelerFields && (
                <p className="entry-fee-alert">{travelerError}</p>
              )}

              {showTravelerFields && (
                <>
                  {scannedPassport && (
                    <p className="entry-fee-scan-note">
                      {t("entryFee.form.scannedNote")}
                    </p>
                  )}
                  <div className="entry-fee-fields">
                    <Input
                      onChange={handleChange(`firstName`)}
                      error={touched.firstName && errors.firstName}
                      onBlur={() => setFieldTouched("firstName")}
                      name="firstName"
                      mandatory
                      type="firstName"
                      label={t("account.firstName")}
                      value={values?.firstName}
                    />
                    <Input
                      onChange={handleChange(`lastName`)}
                      error={touched.lastName && errors.lastName}
                      onBlur={() => setFieldTouched("lastName")}
                      name={"lastName"}
                      mandatory
                      label={t("account.lastName")}
                      value={values?.lastName}
                    />

                    <EntryFeeDateField
                      mandatory
                      label={t("account.dateOfBirth")}
                      value={values.dateOfBirth}
                      onChange={(date) => setFieldValue("dateOfBirth", date)}
                      onBlur={() => setFieldTouched("dateOfBirth")}
                      error={touched.dateOfBirth && errors.dateOfBirth}
                      minDate={new Date(1920, 0, 1)}
                      maxDate={new Date()}
                      openToDate={new Date(1995, 0, 1)}
                      showYearSelect
                    />
                    <SelectOptions
                      mandatory
                      label={t("entryFee.form.gender")}
                      options={gender}
                      name="gender"
                      onChange={handleChange("gender")}
                      error={touched["gender"] && errors["gender"]}
                      value={
                        gender.find((item) => item.value === values["gender"])
                          ?.label
                      }
                      onBlur={() => setFieldTouched("gender")}
                    />
                    <Input
                      onChange={handleChange(`passportNumber`)}
                      error={touched.passportNumber && errors.passportNumber}
                      onBlur={() => setFieldTouched("passportNumber")}
                      name={"passportNumber"}
                      value={values?.passportNumber}
                      label={t("entryFee.form.passportNumber")}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="entry-fee-actions">
              <Button
                type="submit"
                className="entry-fee-action"
                loading={loading}
              >
                {t("entryFee.form.continue")}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </section>
  );
};

export default Apply;
